import cronstrue from "cronstrue";
import produce from "immer";
import { useEffect } from "react";
import {
  Controller,
  FormProvider,
  useController,
  useForm,
  useFormContext,
} from "react-hook-form";

import {
  OrchestrationSchedulerType,
  useListOrchestrationWorkflowsQuery,
} from "@/apollo/types";
import { Alert, AlertIcon } from "@/components/elements/Alert";
import { Button } from "@/components/elements/Button";
import { CronDisplayReadable } from "@/components/elements/CronDisplayReadable";
import { CronInput, TimeInput } from "@/components/elements/SyncScheduler";
import { Heading } from "@/components/elements/Typography";
import { SchedulerSelecter } from "@/components/modules/SchedulerSelecter";
import { TaskAction } from "@/components/modules/TaskActionBar";
import {
  SyncScheduleRadioGroup,
  SyncSchedulerMenu,
  WeldSyncScheduler,
} from "@/components/modules/WeldSyncScheduler";
import FieldLabel from "@/components/primitives/InputLabel";
import {
  OrchestrationFeatureGuard,
  useOrchestrationFeatureGuard,
} from "@/features/feature-guards";
import {
  NextButton,
  PrevButton,
} from "@/widgets/syncs/shared/ui/StepNavButton";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/16/solid";

import { useReverseETLSetupContext } from "../../state/ReverseETLSetupContext";
import { useReverseETLStateContext } from "../../state/StepStateContext";

type FormValues = {
  scheduleKey: string;
  schedulerType: OrchestrationSchedulerType;
  orchestrationWorkflowId?: string;
};

export function SyncSettingsStep() {
  const { state, setState } = useReverseETLStateContext();
  const stepsContext = useReverseETLSetupContext();

  const formMethods = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      ...state.syncSettings.value,
    },
  });

  const { isValid, isSubmitting } = formMethods.formState;

  const schedulerType = formMethods.watch("schedulerType");

  const { isEnabled: isOrchestrationEnabled } = useOrchestrationFeatureGuard();
  const { setValue } = formMethods;
  useEffect(() => {
    if (
      isOrchestrationEnabled === false &&
      schedulerType === OrchestrationSchedulerType.Global
    ) {
      setValue("schedulerType", OrchestrationSchedulerType.Local);
    }
  }, [schedulerType, isOrchestrationEnabled, setValue]);

  return (
    <FormProvider {...formMethods}>
      <div className="mb-6">
        <Heading className="mb-1">Schedule Your Sync</Heading>
        <p className="text-sm text-muted-foreground">
          Decide how often you want your data to sync. Pick a frequency that
          matches your needs to keep your destination always up-to-date.
        </p>
      </div>
      <form
        id="sync-settings-form"
        onSubmit={formMethods.handleSubmit((data) => {
          const newState = produce(state, (draft) => {
            draft.syncSettings.isValid = true;
            draft.syncSettings.value = data;
          });
          setState(newState);
          return stepsContext.createSync(newState);
        })}
        className="w-full pb-20"
      >
        <div>
          <div className="space-y-10">
            <OrchestrationFeatureGuard>
              <Scheduler />
            </OrchestrationFeatureGuard>
            {schedulerType === OrchestrationSchedulerType.Local && (
              <LocalSchedule />
            )}
          </div>
        </div>
        <TaskAction>
          <div className="flex flex-row-reverse gap-4">
            <NextButton
              type="submit"
              form="sync-settings-form"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              loadingText="Starting ..."
            >
              Start Sync
            </NextButton>
            <PrevButton />
          </div>
        </TaskAction>
      </form>
    </FormProvider>
  );
}

function Scheduler() {
  const formContext = useFormContext<FormValues>();

  const { data, loading } = useListOrchestrationWorkflowsQuery();

  const schedulerTypeCtrl = useController({
    name: "schedulerType",
    control: formContext.control,
    rules: {
      required: {
        value: true,
        message: "Please select a schedule",
      },
    },
  });

  const orchestrationWorkflowIdCtrl = useController({
    name: "orchestrationWorkflowId",
    control: formContext.control,
    rules: {
      validate: (value) => {
        if (
          formContext.getValues("schedulerType") ===
            OrchestrationSchedulerType.Global &&
          !value
        ) {
          return "Please select a workflow";
        }
        return true;
      },
    },
  });

  if (loading || !data || data.orchestrationWorkflows.length === 0) {
    return null;
  }
  return (
    <div>
      <FieldLabel>Schedule</FieldLabel>
      <SchedulerSelecter
        hideLabel
        configuration={{
          allowNewWorkflow: false,
          allowNoSchedule: false,
        }}
        orchestrationScheduler={schedulerTypeCtrl.field.value}
        orchestrationWorkflowId={orchestrationWorkflowIdCtrl.field.value}
        onUpdate={(config) => {
          if (!config.orchestrationScheduler) return;
          schedulerTypeCtrl.field.onChange(config.orchestrationScheduler);
          orchestrationWorkflowIdCtrl.field.onChange(
            config.orchestrationWorkflowId,
          );
        }}
      />
    </div>
  );
}

function LocalSchedule() {
  const formContext = useFormContext<FormValues>();
  return (
    <Controller
      name="scheduleKey"
      rules={{
        validate: {
          required: (value) => {
            if (
              formContext.getValues("schedulerType") ===
              OrchestrationSchedulerType.Local
            ) {
              return !!value || "Please select a schedule";
            }
            return true;
          },
          validCron: (value) => {
            try {
              cronstrue.toString(value, { verbose: true });
              return true;
            } catch (e) {
              return "Invalid cron expression";
            }
          },
        },
      }}
      render={({ field }) => (
        <WeldSyncScheduler cron={field.value} onChange={field.onChange}>
          {({ value, showTimeOffsetInput }) => (
            <div className="space-y-6">
              <div>
                <div className="flex items-center justify-between">
                  <FieldLabel>
                    How often do you want to sync data with Weld?
                  </FieldLabel>
                  <SyncSchedulerMenu
                    button={
                      <Menu.Button
                        as={Button}
                        size="xs"
                        variant="link"
                        iconRight={<ChevronDownIcon />}
                      >
                        Advanced Options
                      </Menu.Button>
                    }
                  />
                </div>
                <CronInput />
                <SyncScheduleRadioGroup className="grid grid-cols-2 gap-x-4 gap-y-3" />
              </div>
              {showTimeOffsetInput && (
                <div>
                  <FieldLabel>Starting at</FieldLabel>
                  <TimeInput />
                </div>
              )}
              <div>
                <Alert status="info">
                  <AlertIcon />
                  <div className="space-y-2">
                    <div>
                      <strong>Your data will sync now</strong>, and then
                      automatically according to this schedule:
                    </div>
                    <em>
                      {value && (
                        <CronDisplayReadable verbose cron={value.value} />
                      )}
                    </em>
                  </div>
                </Alert>
              </div>
            </div>
          )}
        </WeldSyncScheduler>
      )}
    />
  );
}
